import { Box, Grid, makeStyles } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { RichGrid } from "../components/table/RichGrid.component";
import { vehiclesTableProvider } from "./vehiclesGrid.columns";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import { Theme as DefaultTheme, Theme } from '@material-ui/core/styles/createMuiTheme';
import { RowHandlers } from "../components/table/Table";
import { useDomainPath } from "../auth/auth.context";
import { get } from "../fetch";

/**
 * Styles pour la grille des véhicules
 * 
 * Styles appliqués :
 * - Fond gris avec coins arrondis pour les véhicules normaux
 * - Fond gris plus foncé pour les véhicules sélectionnés
 * - Fond blanc avec bordure pour les véhicules désactivés
 * - Effet hover avec ombre
 * - Curseur adapté selon l'état (pointer ou not-allowed)
 */
const useStylesGrid = makeStyles<
  DefaultTheme
>((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey['300'],
    borderRadius: '5px',
    margin: theme.spacing(1, 0),
    padding: theme.spacing(0.8, 0.5),
    cursor: 'pointer',
    transition: 'box-shadow 0.3s',

    '&:hover': {
      boxShadow: theme.shadows['4'],
    },
  },
  rootSelected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey['400'],
    borderRadius: '5px',
    margin: theme.spacing(1, 0),
    padding: theme.spacing(0.8, 0.5),
    cursor: 'pointer',
    transition: 'box-shadow 0.3s',

    '&:hover': {
      boxShadow: theme.shadows['4'],
    },
  },
  rootDisabled: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    borderRadius: '5px',
    border: '1px solid lightgrey',
    margin: theme.spacing(1, 0),
    padding: theme.spacing(0.8, 0.5),
    cursor: 'not-allowed',
    transition: 'box-shadow 0.3s',

    '&:hover': {
      boxShadow: theme.shadows['4'],
    },
  }
}));

/**
 * Interface pour les props du composant VehiclesGrid
 */
interface VehiclesGridProps {
  /** Fonction appelée lors de la configuration d'un véhicule */
  onConfigure: (bus: CRM.BusDocument)=>void;
  /** Fonction appelée lors de l'assignation d'un véhicule */
  onAssign: (busId: string)=>void;
  /** Numéro de rafraîchissement pour déclencher le rechargement */
  refresh?: number;
}

/**
 * Composant de grille des véhicules
 * 
 * Ce composant affiche :
 * - La liste de tous les véhicules de la flotte
 * - Les informations de chaque véhicule (nom, capacité, immatriculation, statut)
 * - Actions de configuration et d'assignation
 * - Rafraîchissement automatique lors des modifications
 * 
 * Logique métier :
 * - Récupération des véhicules depuis l'API
 * - Affichage en grille avec colonnes configurables
 * - Gestion des états de chargement
 * - Callbacks pour les actions utilisateur
 * 
 * @param onConfigure Fonction appelée lors de la configuration d'un véhicule
 * @param onAssign Fonction appelée lors de l'assignation d'un véhicule
 * @param refresh Numéro de rafraîchissement pour déclencher le rechargement
 * @returns Composant React de la grille des véhicules
 */
export function VehiclesGrid({
  onConfigure,
  onAssign,
  refresh,
}: VehiclesGridProps) {

  const path = useDomainPath(`/bus`);
  const classesgrid: ClassNameMap<string> = useStylesGrid();
  const [buses, setBuses] = useState<CRM.BusDocument[]>([]);
  const refreshVehicles = useCallback(
    () => get<CRM.BusDocument[]>(`${path}`).then(
      _buses => {
        if (!_buses) {
          return;
        }
      setBuses(_buses);
    }),[path]);
      
  
    useEffect(() => {
      if(refresh) {
        refreshVehicles();
      }
    }, [ refresh, refreshVehicles]);

  return (
    <div>
      <Grid item xs={12}>
        <Box flexGrow={0.85}>

          {<RichGrid
            style={{
              paddingRight: '10px', //compensate for scrollbar width
            }}
            columns={
              vehiclesTableProvider('admin', onAssign, onConfigure)
                .columns
            }
            rows={buses}
            renderRow={bus => {
              return (
                <div className='Grid-row'>
                  <Grid
                    container
                    className={
                      classesgrid.root
                    }
                    spacing={1}
                    onClick={() => {}}
                  >
                    {vehiclesTableProvider('admin', onAssign, onConfigure)
                      .columns.map(column => {
                        const Cell = column.Cell;
                        return (
                          <Grid item key={column.key} xs={column.xs} className={'ellipsis'}>
                            {Cell && <Cell value={bus} handlers={{} as RowHandlers} />}
                            {column.renderCell &&
                              column.renderCell(bus, {} as RowHandlers)}
                          </Grid>
                        );
                      })}
                  </Grid>
                </div>
              );
            }}
          />}
        </Box>
      </Grid>
    </div>
  );
}